import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { Zap } from "lucide-react";

export default function LoginPage() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const hasError = location.search.includes("error=");

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(#6366f1 1px, transparent 1px), linear-gradient(90deg, #6366f1 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Glow effects */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center max-w-md w-full px-6">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Zap size={20} className="text-white" />
          </div>
          <span
            className="text-2xl font-semibold text-slate-50 tracking-tight"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            KubeAnalytics
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-4xl font-semibold text-slate-50 mb-3 leading-tight"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          Kubernetes Intelligence Platform
        </h1>
        <p className="text-base text-slate-400 mb-10 leading-relaxed">
          Monitor clusters, detect failures, optimize costs, and troubleshoot with AI — all in one place.
        </p>

        {/* Error */}
        {hasError && (
          <div className="w-full mb-4 px-4 py-3 rounded-md bg-rose-400/10 border border-rose-400/20 text-rose-400 text-sm">
            Sign-in failed. Please try again.
          </div>
        )}

        {/* Sign-in card */}
        <div className="w-full bg-slate-900/60 border border-slate-800 rounded-md p-8 backdrop-blur-sm">
          <h2
            className="text-lg font-semibold text-slate-100 mb-2"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Sign in to continue
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            Use your Google account to access the platform.
          </p>

          <button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-200 text-slate-950 rounded-md font-semibold text-sm transition-all duration-200 active:scale-[0.98] shadow-sm"
          >
            {/* Google icon */}
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <p className="mt-6 text-xs text-slate-600">
          Demo platform • Simulated Kubernetes data • No real cluster required
        </p>
      </div>
    </div>
  );
}
